import { Alert, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import { convertISODateTimeShort } from "@entrilia/shared/utilities/dateUtils";
import type { EmailAccountInfo } from "../../../api/emailAccountsTypes";
import { api } from "../../../api/operationsApi";
import UserContext from "../../../context/UserContext";
import type { ClientProfile } from "./clientProfilesTypes";
import EmailAccountHeaderCells from "./EmailAccountHeaderCells";
import { sendingProblemLabels } from "./emailAccountsLabels";
import OrganizationEmailAccountRow from "./OrganizationEmailAccountRow";
import SendTestEmailDialog from "./SendTestEmailDialog";
import { getSmtpLoginStatus } from "./smtpSettings";

interface Props {
  organizationCode: string;
  clientsByCode: Map<string, ClientProfile>;
  onSelectClient: (clientCode: string) => void;
}

const OrganizationEmailAccounts = ({ organizationCode, clientsByCode, onSelectClient }: Props) => {
  const { permissions } = useDefinedContext(UserContext);
  const [testEmailAccount, setTestEmailAccount] = useState<EmailAccountInfo>();
  const getEmailAccounts = useCallback(
    () => api.emailAccounts.getOrganizationEmailAccounts(organizationCode),
    [organizationCode]
  );
  const [emailAccounts, fetchError] = useFetch(getEmailAccounts);
  const getSmtpLogins = useCallback(
    () => api.emailAccounts.getOrganizationSmtpLogins(organizationCode),
    [organizationCode]
  );
  const [smtpLogins, smtpLoginsError] = useFetch(getSmtpLogins);

  if (fetchError) {
    logError(fetchError, "[OrganizationEmailAccounts] getOrganizationEmailAccounts");
    return <DataLoadingFailed text={fetchError.message} />;
  }

  if (emailAccounts === undefined) {
    return <InlineLoader />;
  }

  if (smtpLoginsError) {
    logError(smtpLoginsError, "[OrganizationEmailAccounts] getOrganizationSmtpLogins");
  }

  const getClientTitle = (clientCode: string) => clientsByCode.get(clientCode)?.title ?? clientCode;
  const clientsThatCannotSend = emailAccounts.clients.filter((client) => client.sendingProblem);
  const canSendTestEmails = permissions.includes("ManageClientEmailAccounts");

  return (
    <Stack spacing={2}>
      <Stack spacing={1}>
        <Typography variant="subtitle1">Sending Domains</Typography>
        {emailAccounts.domains.length === 0 ? (
          <Typography color="textSecondary">No sending domains are attributed to this organization yet.</Typography>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Domain</TableCell>
                <TableCell>State</TableCell>
                <TableCell>Ownership</TableCell>
                <TableCell>Last refreshed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {emailAccounts.domains.map((domain) => (
                <TableRow key={domain.id}>
                  <TableCell>{domain.domainName}</TableCell>
                  <TableCell>{domain.state}</TableCell>
                  <TableCell>
                    {domain.ownerOrganizationCode?.toLowerCase() === organizationCode.toLowerCase()
                      ? "Owned"
                      : "Shared"}
                  </TableCell>
                  <TableCell>{convertISODateTimeShort(domain.lastRefreshedAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Stack>
      <Stack spacing={1}>
        <Typography variant="subtitle1">Accounts</Typography>
        {clientsThatCannotSend.map((client) => (
          <Alert key={client.clientCode} severity="warning">
            {getClientTitle(client.clientCode)} cannot send:{" "}
            {client.sendingProblem && sendingProblemLabels[client.sendingProblem]}.
          </Alert>
        ))}
        {emailAccounts.accounts.length === 0 ? (
          <Typography color="textSecondary">No email accounts have been imported for this organization yet.</Typography>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <EmailAccountHeaderCells />
                <TableCell>Serves</TableCell>
                <TableCell>Default for</TableCell>
                {canSendTestEmails && <TableCell />}
              </TableRow>
            </TableHead>
            <TableBody>
              {emailAccounts.accounts.map((account) => (
                <OrganizationEmailAccountRow
                  key={account.id}
                  account={account}
                  smtpLoginStatus={getSmtpLoginStatus(smtpLogins, smtpLoginsError !== undefined, account.id)}
                  defaultForClientTitles={emailAccounts.clients
                    .filter((client) => client.defaultAccountId === account.id)
                    .map((client) => getClientTitle(client.clientCode))}
                  getClientTitle={getClientTitle}
                  onSelectClient={onSelectClient}
                  onSendTestEmail={canSendTestEmails ? setTestEmailAccount : undefined}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </Stack>
      {testEmailAccount && (
        <SendTestEmailDialog
          account={testEmailAccount}
          clientCodes={testEmailAccount.clients.map((client) => client.clientCode)}
          getClientTitle={getClientTitle}
          onClose={() => setTestEmailAccount(undefined)}
        />
      )}
    </Stack>
  );
};

export default OrganizationEmailAccounts;
